import { intelBenchmarks, type BenchmarkMetric, type BenchmarksResponse } from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { A, CAP, DASH, n0, onSet, type TabProps } from './common';
import { K, MINUS } from './model';
import './facility.css';
import { BuildLabel } from '../../ui/buildLabels';

/** The design's metric order (aiScenVals BM); anything else the server adds follows. */
export const BENCH_ORDER = [
  'overallRating',
  'qmRating',
  'staffingRating',
  'healthInspectionRating',
  'totalHprd',
  'rnHprd',
  'occupancy',
  'healthSurveyScore',
  'nurseTurnover',
  'rnTurnover',
  'numPenalties',
  'finesDollars',
  'severeDeficiencies',
  'operatingMargin',
  'revenuePerBed',
  'infectionDeficiencies',
];
const DOLLARS = new Set(['finesDollars', 'revenuePerBed']);
const PCT = new Set(['occupancy', 'operatingMargin']);
const STARS = new Set(['overallRating', 'qmRating', 'staffingRating', 'healthInspectionRating']);
const UNIT: Record<string, string> = {
  overallRating: '★',
  qmRating: '★',
  staffingRating: '★',
  healthInspectionRating: '★',
  totalHprd: ' hrs',
  rnHprd: ' hrs',
  nurseTurnover: '%',
  rnTurnover: '%',
};
const BED_Q: Record<string, string | undefined> = { Any: undefined, '<60': '<60', '60–99': '60-99', '100–149': '100-149', '150+': '150+' };

/** A value the V4 design's way (fmtBm): $94.8K, 86%, 3.62 hrs, 4★, 176. */
export const fmtBm = (key: string, x: number) =>
  DOLLARS.has(key)
    ? K(x)
    : PCT.has(key)
      ? (x * 100).toFixed(0).replace('-', MINUS) + '%'
      : (Math.abs(x) >= 100 ? String(Math.round(x)) : Number.isInteger(x) ? String(x) : x.toFixed(STARS.has(key) ? 1 : 2)) + (UNIT[key] ?? '');

/** The peer mean on the hover line (percent metrics as a percentage, the rest as served). */
const fmtMean = (key: string, x: number) => {
  if (PCT.has(key)) return (x * 100).toFixed(0) + '%';
  const a = Math.abs(x);
  const s = DOLLARS.has(key) ? (a / 1000).toFixed(1) + 'K' : a >= 10 ? a.toFixed(1) : a.toFixed(2);
  return (x < 0 ? MINUS : '') + s;
};

/** The peer box's line: the real per-state count when the server grades within the state. */
export function peerLine(type: string, d: BenchmarksResponse | null, state: string): string {
  if (!d) return type + ' · ' + DASH + ' facilities in peer group';
  const lvl = d.peerGroup.level;
  if (lvl === 'state' || lvl === 'state+band') return type + ' · ' + n0(d.n) + ' facilities in peer group (' + (d.peerGroup.state ?? state) + ')';
  if (/^[A-Z]{2}$/.test(state)) return type + ' · ' + n0(d.n) + ' national facilities · too few ' + state + ' peers, so the group is national';
  return type + ' · ' + n0(d.n) + ' facilities in peer group (national)';
}

/**
 * Benchmarks (V4 aiIsBmk, T16390; aiScenVals L31560): one row per CMS metric, the middle
 * 80% of peers as a band, the median tick and this facility's marker coloured better or
 * worse. The peer set is GET /facilities/benchmarks (type, 2-letter state, bed band).
 */
export function BenchmarksTab(p: TabProps) {
  const { st, set, client, f } = p;
  const type = st.bmType || 'SNF';
  const bed = st.bmBed || 'Any';
  const state = /^[A-Z]{2}$/.test(st.bmState ?? '') ? (st.bmState as string) : '';
  const q = useQuery<BenchmarksResponse>(
    (signal) => intelBenchmarks(client, { query: { type, bedBand: BED_Q[bed], state: state || undefined }, signal }),
    [client, type, bed, state],
  );
  const d = q.data;
  const keys = d ? [...BENCH_ORDER.filter((k) => d.metrics[k]), ...Object.keys(d.metrics).filter((k) => !BENCH_ORDER.includes(k))] : [];
  return (
    <div style={sx('display:flex;flex-direction:column;gap:13px;min-width:0;')}>
      <div style={sx('display:flex;align-items:flex-end;gap:16px;flex-wrap:wrap;row-gap:6px;')}>
        <div>
          <div style={sx(CAP + 'margin-bottom:6px;')}>Type</div>
          <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;')}>
            {['SNF', 'AL', 'MC', 'ICF', 'RES', 'HHA', 'HOSPICE'].map((t) => (
              <A
                key={t}
                onClick={onSet(set, { bmType: t })}
                style={
                  'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' +
                  (type === t ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);')
                }
              >
                {t}
              </A>
            ))}
          </div>
        </div>
        <div>
          <div style={sx(CAP + 'margin-bottom:6px;')}>State</div>
          <input
            type="text"
            placeholder="any"
            aria-label="State"
            maxLength={2}
            value={st.bmState ?? ''}
            onChange={(e) => set({ bmState: e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() })}
            style={sx('box-sizing:border-box;width:104px;height:26px;padding:0 10px;border:1px solid rgba(34,39,46,.16);border-radius:7px;background:#FCFAF4;font:inherit;font-size:14px;color:#22272E;text-transform:uppercase;outline:none;')}
          />
        </div>
        <div>
          <div style={sx(CAP + 'margin-bottom:6px;')}>Bed band</div>
          <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;')}>
            {['Any', '<60', '60–99', '100–149', '150+'].map((b) => (
              <A
                key={b}
                onClick={onSet(set, { bmBed: b })}
                style={
                  'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
                  (bed === b ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;border:1px solid rgba(47,92,151,.34);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);')
                }
              >
                {b}
              </A>
            ))}
          </div>
        </div>
      </div>
      <div style={sx('box-sizing:border-box;background:rgba(79,138,214,.1);border:1px solid rgba(47,92,151,.24);border-radius:11px;padding:11px 15px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('display:inline-block;width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
        <span style={sx('font-size:16px;font-weight:700;color:#2F5C97;')}>{peerLine(type, d, state)}</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>{f ? 'marker = ' + f.name : 'no facility selected'}</span>
      </div>
      <div style={sx('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:4px;font-size:14px;color:#54606C;')}>
        <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
          <i style={sx('display:inline-block;width:22px;height:7px;border-radius:3px;background:rgba(79,138,214,.22);')} />
          Middle 80% of peers (p10–p90)
        </span>
        <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
          <i style={sx('display:inline-block;width:2px;height:11px;background:#22272E;')} />
          Typical
        </span>
        <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
          <i style={sx('display:inline-block;width:9px;height:11px;border-radius:2px;background:#1F6B52;')} />
          This facility, better
        </span>
        <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
          <i style={sx('display:inline-block;width:9px;height:11px;border-radius:2px;background:#B3261E;')} />
          worse
        </span>
        <span style={sx('flex:1;')} />
        <span>Hover a row for the low end, typical, high end and average</span>
      </div>
      {q.loading && !d && <Skeleton rows={8} label="Loading the peer group" />}
      {q.error && !d && <ErrorState error={q.error} onRetry={q.reload} />}
      {d && d.note && <div style={sx('font-size:14px;color:#54606C;')}>{d.note}</div>}
      {d && keys.length === 0 && <EmptyState compact title="No peer data for this segment" />}
      {d && keys.length > 0 && (
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:0 22px;border-top:1px solid rgba(34,39,46,.14);')}>
          {keys.map((k) => (
            <MetricRow key={k} k={k} m={d.metrics[k]} marker={f?.metrics[k] ?? null} />
          ))}
        </div>
      )}
    </div>
  );
}

function MetricRow({ k, m, marker }: { k: string; m: BenchmarkMetric; marker: number | null }) {
  const lo = m.p10 ?? null;
  const hi = m.p90 ?? null;
  const med = m.p50 ?? null;
  const has = lo != null && hi != null && med != null && !m.suppressed;
  const span = has ? hi - lo || 1 : 1;
  const at = (x: number) => Math.max(0, Math.min(100, ((x - (lo ?? 0)) / span) * 100));
  const low = m.dir === 'low';
  const cmp = has && marker != null;
  const same = cmp && marker === med;
  const better = cmp && (low ? marker < med! : marker > med!);
  const tone = !cmp || same ? '#54606C' : better ? '#1F6B52' : '#B3261E';
  const verdict = !cmp ? (m.suppressed ? 'too few peers' : '') : same ? 'typical' : better ? '▲ better' : '▼ worse';
  const tip = has
    ? m.label + ': p10 ' + fmtBm(k, lo) + ' · typical ' + fmtBm(k, med) + ' · p90 ' + fmtBm(k, hi) + ' · average ' + (m.mean == null ? DASH : fmtMean(k, m.mean))
    : m.label + (m.suppressed ? ': too few peers to show (n=' + n0(m.n) + ')' : ': no peer data');
  return (
    <div title={tip} className="intel-h-bm" style={sx('display:grid;grid-template-columns:minmax(0,1.25fr) minmax(90px,1fr) 70px;gap:12px;align-items:center;padding:8px 2px;border-bottom:1px solid rgba(34,39,46,.08);')}>
      <div style={sx('min-width:0;')}>
        <div style={sx('display:flex;align-items:center;gap:6px;')}>
          <span style={sx('font-size:16px;font-weight:600;overflow-wrap:break-word;')}>{m.label}</span>
          {m.modeled && <BuildLabel><span style={sx('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;background:rgba(34,39,46,.1);border-radius:3px;padding:1px 5px;')}>MODELED</span></BuildLabel>}
        </div>
        <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{low ? 'lower is better' : 'higher is better'}</div>
      </div>
      <div style={sx('position:relative;height:6px;border-radius:3px;background:rgba(79,138,214,.22);')}>
        {has && <div style={sx('position:absolute;top:-3px;width:2px;height:12px;background:#22272E;left:' + at(med).toFixed(1) + '%;')} />}
        {cmp && <div style={sx('position:absolute;top:-4px;width:10px;height:14px;border-radius:3px;margin-left:-5px;box-shadow:0 0 0 2px #FCFAF4;background:' + tone + ';left:' + at(marker).toFixed(1) + '%;')} />}
      </div>
      <div style={sx('text-align:right;min-width:0;')}>
        <div style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:800;white-space:nowrap;color:' + tone + ';')}>{marker == null ? DASH : fmtBm(k, marker)}</div>
        <div style={sx('font-size:14px;font-weight:700;white-space:nowrap;color:' + tone + ';')}>{verdict}</div>
      </div>
    </div>
  );
}
